import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LocalizationProvider, DateField } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import 'dayjs/locale/es';
import { useSnackbar } from 'notistack';
import { getDataFull, postData } from '../../services/ajax';
import { setYupLocaleES } from '../../const/yup';

dayjs.extend(customParseFormat);
setYupLocaleES();

function prefillFromQuery(initialQuery) {
    const q = (initialQuery || '').trim();
    const isDni = /^\d+$/.test(q);
    return {
        nombre: isDni ? '' : q,
        dni: isDni ? q : '',
        fecha_nacimiento: null,
        celular: '',
        email: '',
    };
}

const schema = yup.object({
    nombre: yup.string().required('El nombre es requerido').max(255, 'Máximo 255 caracteres'),
    dni: yup.string().nullable().max(20, 'Máximo 20 caracteres'),
    fecha_nacimiento: yup
        .mixed()
        .nullable()
        .test('not-future', 'La fecha no puede ser futura', (v) => {
            if (v == null || v === '') return true;
            const d = dayjs.isDayjs(v) ? v : dayjs(v);
            if (!d.isValid()) return false;
            return !d.isAfter(dayjs(), 'day');
        }),
    celular: yup.string().nullable().max(40, 'Máximo 40 caracteres'),
    email: yup.string().nullable().email('Debe ser un email válido').max(255),
}).test('contacto', 'Corresponde completar al menos un medio de contacto.', function (values) {
    const celular = (values?.celular || '').trim();
    const email = (values?.email || '').trim();
    if (celular || email) return true;
    return this.createError({ path: 'celular', message: 'Corresponde completar al menos un medio de contacto.' });
});

/**
 * Alta rápida de paciente desde el wizard de nueva orden.
 */
export default function CrearPacienteDialog({
    open,
    onClose,
    token,
    initialQuery = '',
    onCreated,
    onSelectExisting,
}) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { enqueueSnackbar } = useSnackbar();
    const [dniMatch, setDniMatch] = useState(null);
    const [checkingDni, setCheckingDni] = useState(false);

    const initialValues = useMemo(() => prefillFromQuery(initialQuery), [initialQuery]);

    useEffect(() => {
        if (!open) setDniMatch(null);
    }, [open]);

    const checkDniExact = async (dniRaw) => {
        const dni = (dniRaw || '').trim();
        if (dni.length < 6) {
            setDniMatch(null);
            return;
        }
        setCheckingDni(true);
        try {
            const res = await getDataFull(
                `pacientes?search_text=${encodeURIComponent(dni)}&per_page=20`,
                token
            );
            if (res.error !== '') {
                setDniMatch(null);
                return;
            }
            const list = Array.isArray(res.data) ? res.data : [];
            const exact = list.find((p) => String(p.dni || '') === dni) || null;
            setDniMatch(exact);
        } finally {
            setCheckingDni(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen={fullScreen}
            fullWidth
            maxWidth="sm"
            aria-labelledby="crear-paciente-dialog-title"
        >
            <Formik
                key={open ? `crear-paciente-${initialQuery}` : 'crear-paciente-closed'}
                enableReinitialize
                initialValues={initialValues}
                validationSchema={schema}
                onSubmit={async (values, { setSubmitting }) => {
                    const celular = (values.celular || '').trim();
                    const email = (values.email || '').trim();
                    const payload = {
                        nombre: values.nombre.trim(),
                        dni: (values.dni || '').trim() || null,
                        celular: celular || null,
                        email: email || null,
                        fecha_nacimiento: (() => {
                            const v = values.fecha_nacimiento;
                            if (!v) return null;
                            const d = dayjs.isDayjs(v) ? v : dayjs(v);
                            return d.isValid() ? d.format('YYYY-MM-DD') : null;
                        })(),
                    };

                    try {
                        const response = await postData('pacientes', payload, token);
                        if (response.error === '') {
                            onCreated?.(response.data);
                            onClose();
                        } else {
                            enqueueSnackbar(response.error || 'No se pudo crear el paciente.', { variant: 'error' });
                        }
                    } catch (e) {
                        enqueueSnackbar('No se pudo crear el paciente.', { variant: 'error' });
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => (
                    <Form>
                        <DialogTitle id="crear-paciente-dialog-title">Nuevo paciente</DialogTitle>
                        <DialogContent>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Se usa cuando el paciente no figura en la búsqueda. El contacto permite avisar el turno.
                            </Typography>

                            {dniMatch ? (
                                <Alert
                                    severity="warning"
                                    sx={{ mb: 2 }}
                                    action={
                                        <Button
                                            color="inherit"
                                            size="small"
                                            onClick={() => {
                                                onSelectExisting?.(dniMatch);
                                                onClose();
                                            }}
                                        >
                                            Seleccionar paciente existente
                                        </Button>
                                    }
                                >
                                    Ya figura un paciente con ese DNI.
                                </Alert>
                            ) : null}

                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                                <Stack spacing={2} sx={{ mt: 0.5 }}>
                                    <TextField
                                        name="nombre"
                                        label="Nombre"
                                        variant="standard"
                                        fullWidth
                                        autoFocus
                                        value={values.nombre}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.nombre && Boolean(errors.nombre)}
                                        helperText={
                                            (touched.nombre && errors.nombre)
                                            || 'Nombre completo del paciente.'
                                        }
                                    />
                                    <TextField
                                        name="dni"
                                        label="DNI"
                                        variant="standard"
                                        fullWidth
                                        value={values.dni}
                                        onChange={handleChange}
                                        onBlur={(e) => {
                                            handleBlur(e);
                                            checkDniExact(e.target.value);
                                        }}
                                        error={touched.dni && Boolean(errors.dni)}
                                        helperText={
                                            (touched.dni && errors.dni)
                                            || (checkingDni ? 'Verificando DNI…' : 'Documento opcional; se verifica si ya está registrado.')
                                        }
                                    />
                                    <DateField
                                        label="Fecha de nacimiento"
                                        variant="standard"
                                        fullWidth
                                        format="DD/MM/YYYY"
                                        value={values.fecha_nacimiento}
                                        onChange={(val) => setFieldValue('fecha_nacimiento', val)}
                                        slotProps={{
                                            textField: {
                                                error: touched.fecha_nacimiento && Boolean(errors.fecha_nacimiento),
                                                helperText: (touched.fecha_nacimiento && errors.fecha_nacimiento)
                                                    || 'Formato DD/MM/AAAA. Opcional.',
                                            },
                                        }}
                                    />
                                    <TextField
                                        name="celular"
                                        label="Celular"
                                        variant="standard"
                                        fullWidth
                                        value={values.celular}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.celular && Boolean(errors.celular)}
                                        helperText={
                                            (touched.celular && errors.celular)
                                            || 'Corresponde completar al menos un medio de contacto.'
                                        }
                                    />
                                    <TextField
                                        name="email"
                                        label="Correo electrónico"
                                        variant="standard"
                                        fullWidth
                                        value={values.email}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.email && Boolean(errors.email)}
                                        helperText={
                                            (touched.email && errors.email)
                                            || 'Alternativa o complemento al celular para el aviso de turno.'
                                        }
                                    />
                                </Stack>
                            </LocalizationProvider>
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 2 }}>
                            <Button onClick={onClose} disabled={isSubmitting}>Cancelar</Button>
                            <Button type="submit" variant="contained" color="secondary" disabled={isSubmitting}>
                                {isSubmitting ? 'Creando…' : 'Crear y continuar'}
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
}
